import React, { Component, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { logger } from '@/utils/logger'

interface Props {
    children: ReactNode
    fallback?: ReactNode
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void
    showDetails?: boolean
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: React.ErrorInfo | null
    errorId: string
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: ''
        }
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        // Generate unique error ID for tracking
        const errorId = `error_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
        
        return {
            hasError: true,
            error,
            errorId
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({ errorInfo })

        // Log error with our logging system
        logger.error('component', 'React Error Boundary caught error', {
            error: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            errorId: this.state.errorId
        })

        // Call custom error handler if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo)
        }

        // Report to error tracking service (if you have one)
        this.reportErrorToService(error, errorInfo)
    }

    private reportErrorToService(error: Error, errorInfo: React.ErrorInfo) {
        // Example integration with error tracking services
        // You can integrate with Sentry, LogRocket, or similar services here
        if (process.env.NODE_ENV === 'production') {
            // Example: Sentry.captureException(error, { contexts: { react: errorInfo } })
            logger.error('component', 'Error reported to tracking service', {
                errorId: this.state.errorId
            })
        }
    }

    private handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: ''
        })
    }

    private handleReload = () => {
        window.location.reload()
    }

    private handleGoHome = () => {
        window.location.href = '/'
    }

    render() {
        if (this.state.hasError) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback
            }

            // Default error UI
            return (
                <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
                    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
                        <div className="mb-4">
                            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
                            <h1 className="text-xl font-semibold text-gray-900 mb-2">
                                Oops! Something went wrong
                            </h1>
                            <p className="text-gray-600 text-sm mb-4">
                                We're sorry, but something unexpected happened. Don't worry - it's not your fault!
                            </p>
                        </div>

                        <div className="space-y-3">
                            <button
                                onClick={this.handleRetry}
                                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <RefreshCw className="h-4 w-4" />
                                Try Again
                            </button>
                            
                            <button
                                onClick={this.handleGoHome}
                                className="w-full flex items-center justify-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                <Home className="h-4 w-4" />
                                Go to Homepage
                            </button>
                            
                            <button
                                onClick={this.handleReload}
                                className="w-full text-gray-500 hover:text-gray-700 transition-colors text-sm"
                            >
                                Reload Page
                            </button>
                        </div>

                        {this.props.showDetails && process.env.NODE_ENV === 'development' && (
                            <details className="mt-6 text-left">
                                <summary className="cursor-pointer text-xs text-gray-500 mb-2">
                                    Technical Details (Development Only)
                                </summary>
                                <div className="bg-gray-100 rounded p-3 text-xs text-gray-700">
                                    <p className="font-semibold mb-1">Error ID: {this.state.errorId}</p>
                                    <p className="font-semibold mb-1">Error:</p>
                                    <p className="mb-3 font-mono">{this.state.error?.message}</p>
                                    <p className="font-semibold mb-1">Component Stack:</p>
                                    <pre className="whitespace-pre-wrap font-mono text-xs">
                                        {this.state.errorInfo?.componentStack}
                                    </pre>
                                </div>
                            </details>
                        )}

                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-xs text-gray-500">
                                Error ID: {this.state.errorId}
                            </p>
                        </div>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary